import { createGlobalState } from '@vueuse/core';
import { computed, nextTick, ref } from 'vue';
import { useApi } from '@/composables/useApi.ts';
import { useMessageStore } from '@/stores/message.ts';
import i18n from '@/plugins/i18n';

export interface ResendTarget {
  documentId: string;
  signeeId: string;
  email: string;
}

function showSnackbar(message: { key?: string; text?: string; color: string }) {
  useMessageStore().showMessage({
    ...message,
    icon:
      message.color === 'success'
        ? 'fa fa-circle-check'
        : 'fa fa-circle-exclamation'
  });
}

export const useResendInvitations = createGlobalState(() => {
  const { deepSignAPI } = useApi();

  const showDialog = ref(false);
  const sending = ref(false);
  const targets = ref<ResendTarget[]>([]);

  const isPending = computed(() => sending.value);
  const isSingle = computed(() => targets.value.length === 1);

  function openDialog(nextTargets: ResendTarget[]) {
    targets.value = nextTargets;
    showDialog.value = true;
  }

  function closeDialog() {
    showDialog.value = false;
  }

  function showSendError() {
    showSnackbar({
      key: 'errors.api.send_reminder',
      color: 'error'
    });
  }

  function showSuccess(sentTargets: ResendTarget[]) {
    if (sentTargets.length === 1) {
      showSnackbar({
        text: i18n.global.t('texts.reminder_successfully_sent', {
          email: sentTargets[0].email
        }),
        color: 'success'
      });
      return;
    }
    showSnackbar({
      text: i18n.global.t('texts.reminders_successfully_sent'),
      color: 'success'
    });
  }

  function requestResendForSignee(target: ResendTarget) {
    if (!target.documentId || !target.signeeId || !target.email) return;
    if (isPending.value) return;
    openDialog([target]);
  }

  async function confirmResend() {
    if (sending.value || !targets.value.length) return;
    sending.value = true;
    const sentTargets = [...targets.value];
    try {
      await Promise.all(
        sentTargets.map((target) =>
          deepSignAPI.value.documentsSigneesResendInvitation.create(
            target.documentId,
            target.signeeId
          )
        )
      );
      // Close first: Vuetify tears down the dialog overlay and that dismisses
      // a snackbar that was opened while the dialog was still on screen.
      closeDialog();
      await nextTick();
      showSuccess(sentTargets);
    } catch (error) {
      console.error('Resend invitations: send failed', error);
      showSendError();
    } finally {
      sending.value = false;
    }
  }

  return {
    showDialog,
    sending,
    targets,
    isPending,
    isSingle,
    requestResendForSignee,
    confirmResend,
    closeDialog
  };
});
